import { FileText } from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';

const SubscriptionCurrentPlanCard = () => {
  const {
    effectiveSub,
    currentPlan,
    isTrialUser,
    isTrialExpired,
    allPlans,
    formatDate,
    formatCurrency,
    navigate
  } = useProfile();

  return (
    <section className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 p-4 sm:p-6 lg:p-7 shadow-sm">
      <div className="flex justify-between items-center border-b border-slate-100 pb-3.5 sm:pb-4 mb-3.5 sm:mb-4 gap-2">
        <div>
          <h2 className="font-semibold text-slate-900 text-sm sm:text-base">Current plan</h2>
        </div>
        <button
          onClick={() => navigate('/subscription')}
          className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#067978] hover:bg-[#05605f] text-white rounded-xl font-bold text-xs shadow-md shadow-[#067978]/20 transition-all active:scale-95 cursor-pointer whitespace-nowrap"
        >
          Upgrade Now
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#067978] text-white rounded-xl flex items-center justify-center shrink-0 shadow-xs">
            <FileText size={20} className="sm:w-[22px] sm:h-[22px]" />
          </div>
          <div>
            <div className="font-serif text-xl sm:text-2xl font-medium text-[#067978] leading-tight">
              {isTrialUser
                ? 'Trial Period - Growth'
                : (effectiveSub.plan_id?.name || currentPlan?.name || (typeof effectiveSub.plan_id === 'string' ? allPlans.find(p => p._id === effectiveSub.plan_id)?.name : null) || 'Pro')}
            </div>
          </div>
        </div>
        <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-1">
          {isTrialUser ? (
            effectiveSub.status === 'trial' && !isTrialExpired ? (
              !effectiveSub.trial_start ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-normal text-teal-700 bg-teal-50 px-2.5 py-0.5 rounded-full border border-teal-200/60 whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                  Starts on 1st Voucher
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-normal text-teal-700 bg-teal-50 px-2.5 py-0.5 rounded-full border border-teal-200/60 whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-600"></span>
                  Active Trial - Growth
                </span>
              )
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-normal text-[#067978] whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-[#067978]"></span>
                Expired
              </span>
            )
          ) : effectiveSub.status === 'active' ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-normal text-emerald-600 whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-normal text-[#067978] whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-[#067978]"></span>
              {effectiveSub.status === 'expired' ? 'Expired' : effectiveSub.status === 'cancelled' ? 'Cancelled' : effectiveSub.status || 'Active'}
            </span>
          )}

          {isTrialUser ? (
            <div className="flex flex-col items-start sm:items-end text-left sm:text-right">
              <span className="text-xs text-slate-600 font-semibold">
                {(() => {
                  if (!effectiveSub.trial_start) {
                    return '30-Day Free Trial - Growth';
                  }
                  const trialEnd = effectiveSub.trial_end || effectiveSub.current_period_end;
                  const days = trialEnd ? Math.max(0, Math.ceil((new Date(trialEnd) - new Date()) / (1000 * 60 * 60 * 24))) : 0;
                  return (days > 0 && !isTrialExpired) ? `${days} days left` : 'Trial ended';
                })()}
              </span>
              <span className="text-[11px] text-slate-400 font-normal mt-0.5">
                {effectiveSub.trial_start && effectiveSub.trial_end
                  ? `Ends on ${formatDate(effectiveSub.trial_end)}`
                  : 'Starts on 1st voucher creation'}
              </span>
            </div>
          ) : (
            <span className="text-xs text-slate-500 font-normal">
              {effectiveSub.billing_interval === 'year' ? 'Annual plan' : effectiveSub.billing_interval === '6months' ? '6 Months plan' : 'Monthly plan'}
            </span>
          )}
        </div>
      </div>

      {/* Upcoming Plan Notice for Trial Members */}
      {effectiveSub?.status === 'trial' && effectiveSub?.upcoming_plan_id && (
        <div className="mt-4 p-3.5 sm:p-4 rounded-2xl bg-teal-50/50 border border-teal-100/80 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-xs sm:text-sm text-slate-800">
              Subscribed to {effectiveSub.upcoming_plan_id.name || 'Upgraded'} Plan
            </h4>
            <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/70 text-[11px] font-medium">
              Starts {formatDate(effectiveSub?.trial_end)}
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed font-normal">
            Thank you for being our valuable partner! You have already subscribed to the {effectiveSub.upcoming_plan_id.name} Plan ({effectiveSub?.billing_interval === 'year' ? 'Annual' : effectiveSub?.billing_interval === '6months' ? '6 Months' : 'Monthly'}). Once your trial ends on {formatDate(effectiveSub?.trial_end)}, your plan will start automatically so you can seamlessly enjoy all benefits without interruption!
          </p>
        </div>
      )}

      {/* Metric Sections for Subscriptions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-0 border-t border-slate-100 mt-4 sm:mt-5 pt-3.5 sm:pt-4">
        <div className="sm:pr-4">
          <span className="block text-[11px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">
            {isTrialUser
              ? 'Trial End Date'
              : (effectiveSub.status === 'cancelled'
                  ? 'Access Ends'
                  : (effectiveSub.razorpay_subscription_id ? 'Next billing' : 'Expiry / Validity'))}
          </span>
          <b className="text-slate-800 font-semibold text-sm sm:text-base block">
            {isTrialUser
              ? (effectiveSub.trial_start && effectiveSub.trial_end ? formatDate(effectiveSub.trial_end) : 'Pending (on 1st voucher)')
              : formatDate(
                  (effectiveSub.next_billing_at && (!effectiveSub.start_date || new Date(effectiveSub.next_billing_at) > new Date(effectiveSub.start_date)))
                    ? effectiveSub.next_billing_at
                    : (effectiveSub.end_date || effectiveSub.next_billing_at || effectiveSub.nextBillingAt)
                )}
          </b>
        </div>
        <div className="sm:px-4 border-t sm:border-t-0 sm:border-l border-slate-100 pt-2.5 sm:pt-0">
          <span className="block text-[11px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">
            {isTrialUser ? 'Plan Cost' : 'Amount'}
          </span>
          {isTrialUser ? (
            <b className="text-slate-800 font-semibold text-sm sm:text-base block">
              Free <small className="text-emerald-600 font-medium text-xs">(30-Day Trial - Growth)</small>
            </b>
          ) : (() => {
            const plan = effectiveSub?.plan_id;
            const isYearly = effectiveSub?.billing_interval === 'year';
            const gstPercentage = plan?.gst_percentage ?? 18;
            const isCustom = effectiveSub?.plan_type === 'custom' && Boolean(effectiveSub?.custom_price !== null && effectiveSub?.custom_price !== undefined && effectiveSub?.custom_price > 0);

            if (effectiveSub?.current_amount) {
              return (
                <div>
                  <b className="text-slate-800 font-semibold text-sm sm:text-base block">
                    {formatCurrency(effectiveSub.current_amount)} <small className="text-slate-500 font-normal text-xs">{isYearly ? '/ year' : '/ mo'} (incl. GST)</small>
                  </b>
                  {plan && plan.yearly_price && plan.yearly_price !== effectiveSub.current_amount && (
                    <span className="text-[10px] text-slate-400 block font-normal mt-0.5">
                      Catalogue: {formatCurrency(isYearly ? (plan.yearly_price || 14390) : (plan.monthly_price || 1499))} {isYearly ? '/ yr' : '/ mo'} + GST
                    </span>
                  )}
                </div>
              );
            }

            let displayPrice;
            let unitLabel;

            if (isCustom) {
              displayPrice = effectiveSub.custom_price;
              unitLabel = '/ mo';
            } else if (isYearly) {
              displayPrice = plan?.yearly_price || (plan?.slug === 'growth' ? 33590 : plan?.slug === 'starter' ? 6710 : 14390);
              unitLabel = '/ year';
            } else {
              displayPrice = plan?.monthly_price || (plan?.slug === 'growth' ? 3499 : plan?.slug === 'starter' ? 699 : 1499);
              unitLabel = '/ mo';
            }

            return (
              <b className="text-slate-800 font-semibold text-sm sm:text-base block">
                {formatCurrency(displayPrice)} <small className="text-slate-500 font-normal text-xs">{unitLabel} {isCustom ? '(Custom Deal)' : `+ ${gstPercentage}% GST`}</small>
              </b>
            );
          })()}
        </div>
      </div>
    </section>
  );
};

export default SubscriptionCurrentPlanCard;
